import { useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNewsReportPreviewResource } from "../../../news/hooks/useNewsReportPreviewResource";
import { useGetBuildings } from "../../../buildings/services/buildingService";
import { useGetReportTypes } from "../../../../shared/services/catalogService";
import { useDeleteNew } from "../../../news/services/newService";
import type { NewReportDataItem } from "../../../../api/client/types/new.types";
import { ROUTES } from "../../../../routes/routes";
import { toast } from "react-hot-toast";
import { useSaveScrollOnReturn } from "../../../../shared/hooks/useSaveScrollOnReturn";
import { NEWS_PREVIEW_SCROLL_KEY } from "../../../../shared/utils/sessionStorageKeys";

export const useNewsPreviewTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const newsReportPreviewResource = useNewsReportPreviewResource();
  const { data: buildings } = useGetBuildings({ page_size: 1000 });
  const { data: reportTypes } = useGetReportTypes();
  const deleteNewMutation = useDeleteNew();
  const saveScrollBeforeNavigate = useSaveScrollOnReturn(NEWS_PREVIEW_SCROLL_KEY);

  // Confirmation modal state
  const [newToDelete, setNewToDelete] = useState<string | null>(null);

  // Building options for select dropdown
  const buildingOptions = useMemo(() => {
    const options = [{ value: "", label: "Todas las unidades" }];
    if (buildings?.items) {
      buildings.items.forEach((building) => {
        options.push({
          value: building.id,
          label: building.name,
        });
      });
    }
    return options;
  }, [buildings]);

  // Report type options for select dropdown
  const reportTypeOptions = useMemo(() => {
    const options = [{ value: "", label: "Todos los tipos" }];
    if (reportTypes) {
      reportTypes.forEach((reportType) => {
        options.push({
          value: String(reportType.id),
          label: reportType.name,
        });
      });
    }
    return options;
  }, [reportTypes]);

  // Open confirmation modal
  const handleOpenDeleteModal = (newId: string) => {
    setNewToDelete(newId);
  };

  // Close confirmation modal
  const handleCloseDeleteModal = () => {
    setNewToDelete(null);
  };

  const handleEditNew = useCallback(
    (item: NewReportDataItem) => {
      saveScrollBeforeNavigate(() =>
        navigate(ROUTES.NEWS.EDIT(item.new_id), {
          state: { returnSearch: location.search || "?tab=novedades" },
        })
      );
    },
    [navigate, location.search, saveScrollBeforeNavigate]
  );

  // Confirm deletion
  const handleConfirmDelete = async () => {
    if (!newToDelete) return;

    try {
      await deleteNewMutation.mutateAsync(newToDelete);
      toast.success("Novedad eliminada exitosamente");
      handleCloseDeleteModal();
      newsReportPreviewResource.refetch();
    } catch (error) {
      toast.error("Error al eliminar la novedad");
      console.error("Error deleting news:", error);
    }
  };

  return {
    newsReportPreviewResource,
    buildingOptions,
    reportTypeOptions,
    handleEditNew,
    // Delete modal state
    isDeleteModalOpen: newToDelete !== null,
    isDeletingNew: deleteNewMutation.isPending,
    // Delete handlers
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleConfirmDelete,
  };
};
